import { useCallback, useEffect, useState } from 'react';
import { Box } from '@coolblue-development/becky';
import EmailEditor from './EmailEditor';
import DraftAttachmentChips from './DraftAttachmentChips';
import { useEmailContext } from '../../../../../../contexts/EmailContext';
import { Alert } from '@coolblue-development/freddy';
import { OutboundEmailFields } from '../../Outbound/EmailOutboundHeader';
import { useAttachmentUpload } from './useAttachmentUpload';
import { renderAttachmentErrorMessage } from './attachmentUploadErrors';

interface ReplyInputProps {
  onDiscard: () => void;
  onSuccess: () => void;
  onError: (message: string) => void;
  onAttachmentUploadError?: (message: string) => void;
  isSendDisabled?: boolean;
  outboundFields?: OutboundEmailFields;
}

const ReplyInput = ({
  onDiscard,
  onSuccess,
  onError,
  onAttachmentUploadError,
  isSendDisabled,
  outboundFields
}: ReplyInputProps): JSX.Element => {
  const {
    clearEmailDraft,
    selectedEmail,
    setTemplateHtml,
    emailData,
    emailDrafts = {},
    outboundContactId,
    updateEmailDraft = () => {}
  } = useEmailContext();
  const [bodySizeExceeded, setBodySizeExceeded] = useState(false);
  const attachments = selectedEmail ? (emailDrafts[selectedEmail]?.attachments ?? []) : [];

  const replyDraftContactId = selectedEmail ? emailData[selectedEmail]?.replyDraftContactId : undefined;
  const uploadContactId = outboundFields ? outboundContactId : replyDraftContactId;

  const { attachmentError, isUploadingAttachments, handleFilesSelected, clearAttachmentError } = useAttachmentUpload({
    selectedEmail,
    uploadContactId,
    attachments,
    updateEmailDraft,
    onUploadError: onAttachmentUploadError
  });

  useEffect(() => {
    setBodySizeExceeded(false);
  }, [selectedEmail]);

  const handleDiscard = useCallback(() => {
    clearAttachmentError();

    if (selectedEmail) {
      clearEmailDraft(selectedEmail);
    } else {
      setTemplateHtml('');
    }

    onDiscard();
  }, [clearAttachmentError, clearEmailDraft, onDiscard, selectedEmail, setTemplateHtml]);

  return (
    <Box
      sx={{
        padding: '12px',
        marginBottom: '16px',
        borderRight: '1px solid #DDDDDD',
        borderLeft: '1px solid #DDDDDD',
        borderBottom: '1px solid #DDDDDD',
        borderRadius: '0 0 4px 4px',
        backgroundColor: '#FFFFFF'
      }}
    >
      <DraftAttachmentChips attachments={attachments} />

      {isUploadingAttachments && (
        <Box sx={{ marginBottom: '12px', color: '#61656B' }} data-testid="attachment-upload-loading">
          Uploading attachments...
        </Box>
      )}

      {bodySizeExceeded && (
        <Alert description="The inserted image is too large and exceeds the size limit of 5MB." intent="error" />
      )}
      {attachmentError && <Alert description={renderAttachmentErrorMessage(attachmentError)} intent="error" />}

      <EmailEditor
        onDiscard={handleDiscard}
        onSuccess={onSuccess}
        onError={onError}
        onBodySizeExceeded={setBodySizeExceeded}
        attachments={attachments}
        onFilesSelected={handleFilesSelected}
        isSendDisabled={isSendDisabled || isUploadingAttachments}
        isUploadingAttachments={isUploadingAttachments}
        isAttachmentUploadDisabled={!uploadContactId}
        outboundFields={outboundFields}
      />
    </Box>
  );
};

export default ReplyInput;
